/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { useLocationStore } from '@ahm/react-map/store';
import { toast } from '@ahm/react-ui';
import {
  ActionIcon,
  Button,
  Card,
  Group,
  List,
  Space,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { ArrowLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import Sortable from 'sortablejs';
import PathBtn from './path-btn';
import { ModalName } from '@/components/modals';
import { useOptimizedRouteHook } from '@/hooks';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type PathDndProps = {
  opened: boolean;
  handleClose?: () => void;
  onSubmit?: (sortedIndexes: number[] | null) => void;
};

export default function PathDnd({
  opened = false,
  handleClose,
  onSubmit,
}: PathDndProps) {
  const isMobile = useIsMobile();
  const tBooking = useTranslations('booking');
  const tCommon = useTranslations('common');
  const paths = useLocationStore.use.paths();
  const { estimatedData, selectedService, setPrevSelectedService } =
    useBookingStore((state) => state);
  const [statePathSortIndexes, setStatePathSortIndexes] = useState<
    number[] | null
  >(null);
  const storePathSortIndexes = useLocationStore.use.setPathSortIndexes();
  const { handleOptimizeRoutes } = useOptimizedRouteHook();
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    let sortable: Sortable;

    if (listRef.current) {
      sortable = new Sortable(listRef.current, {
        animation: 150,
        handle: '.sortable-handler',
        onEnd: (event) => {
          const newEndList = event.from.children;
          const newIndexes = Array.from(newEndList).map((element) =>
            Number(element.getAttribute('data-sort-id'))
          );
          setStatePathSortIndexes(newIndexes);
          storePathSortIndexes(newIndexes);
        },
      });
    }

    return () => {
      if (sortable) sortable.destroy();
    };
  }, [opened, paths]);

  const handleCloseDnd = () => {
    storePathSortIndexes(null);
    handleClose && handleClose();
  };

  const handleSubmit = () => {
    if (onSubmit) onSubmit(statePathSortIndexes);
    storePathSortIndexes(null);
    if (selectedService) setPrevSelectedService(selectedService);
    if (isMobile) {
      handleClose && handleClose();
    }

    toast.success({
      title: 'Đã cập nhật thứ tự điểm giao',
      withIcon: true,
      closeOnClick: false,
    });
  };

  if (!paths) return <></>;

  return (
    <>
      <Group
        pos="relative"
        justify="space-between"
        wrap="nowrap"
        px={isMobile ? 0 : 'sm'}
        py={3}
        mt={8}
      >
        <Group justify="left" wrap="nowrap" gap={0} ml={isMobile ? 0 : -6}>
          {!isMobile && (
            <ActionIcon
              size={44}
              variant="transparent"
              onClick={handleCloseDnd}
            >
              <ArrowLeft width={24} height={24} />
            </ActionIcon>
          )}
          <Title order={isMobile ? 5 : 4}>
            {isMobile
              ? tBooking('location.route')
              : tCommon('click_and_drag_to_rearrange')}
            <Space display="inline-block" w={8} />
            {estimatedData?.data?.distance ? (
              <Text size="sm" fw={700} c="neutral.5" component="span">
                {estimatedData.data.distance}km
              </Text>
            ) : undefined}
          </Title>
        </Group>
        <Button
          px={rem(8)}
          mr={-2}
          fullWidth={paths.length <= 2}
          variant="subtle"
          c="primary.7"
          size={isMobile ? 'compact-sm' : 'compact-md'}
          fw="700"
          style={{ flex: 'none' }}
          onClick={
            isMobile
              ? () => {
                  if (typeof handleOptimizeRoutes === 'function') {
                    handleOptimizeRoutes();
                    closeModal(ModalName.DndPath);
                  }
                }
              : () => {
                  if (typeof handleSubmit === 'function') {
                    handleSubmit();
                  }
                }
          }
        >
          {isMobile ? tBooking('location.optimize_route') : tCommon('save')}
        </Button>
      </Group>

      <List
        listStyleType="none"
        p={0}
        h={isMobile ? '45vh' : '100%'}
        mb={isMobile ? '56px' : '0px'}
        styles={{
          root: {
            overflow: 'auto',
          },
        }}
        ref={listRef}
      >
        {paths?.map((path, idx) => (
          <List.Item
            styles={{
              itemWrapper: { width: '100%' },
              itemLabel: { width: '100%' },
            }}
            key={idx}
            data-sort-id={idx}
          >
            <PathBtn path={path} pathIndex={idx} dndMode />
          </List.Item>
        ))}
      </List>
      {isMobile && (
        <Card
          shadow="sm"
          pos="fixed"
          right={0}
          bottom={0}
          left={0}
          w="100%"
          p="md"
          bg="white"
        >
          <Button
            onClick={handleSubmit}
            fullWidth
            mih={48}
            radius="md"
            type="submit"
          >
            {tCommon('confirm')}
          </Button>
        </Card>
      )}
    </>
  );
}
